<template>
  <div
    v-element-value="localContent"
    :class="{'hkl-invalid-input': invalidInput}"
  >
    <Editor
      :key="reRenderKey"
      v-model="localContent"
      :disabled="disabled"
      apiKey="c72e8nve59b7odpled4jq6q5orbsucf4ww6fa8nhm1kifw5z"
      :init="initConfig"
      :plugins="pluginsOpts"
      :toolbar="toolbarOpts"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

// Import TinyMCE
import tinymce from 'tinymce/tinymce'

import Editor from '@tinymce/tinymce-vue'

/* Default icons are required for TinyMCE 5.3 or above */
import 'tinymce/icons/default'

/* A theme is also required */
import 'tinymce/themes/silver'

/* Import the skin */
import 'tinymce/skins/ui/oxide/skin.css'

/* Import plugins */
import 'tinymce/plugins/code'
import 'tinymce/plugins/directionality'
import 'tinymce/plugins/paste'
import 'tinymce/plugins/colorpicker'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/image'
import 'tinymce/plugins/link'
import 'tinymce/plugins/table'

import { zhCN } from './langs/zh_CN'
import { DEFAULT_TOOLBAR } from './consts'
import httpHeaderMixin from '../httpHeaderMixin'

import { utils as formUtils } from '@/plugin/InputChange'
import { uploadImage } from '@/api/docs/request'
import { langs } from '@/utils/i18n'
import * as util from './util'
import { useBaseUrl } from '@/utils/http/hook'

export default {
  name: 'TinymceEditor',
  components: {
    Editor,
  },
  mixins: [httpHeaderMixin],
  model: {
    prop: 'content',
    event: 'syncContent',
  },
  props: {
    content: {
      type: String,
      default: null,
    },
    height: {
      type: Number,
      required: false,
      default: 360,
    },
    additionalToolbar: {
      type: Array,
      required: false,
      default: () => ([]),
    },
    /*
    * Support toolbar button
    * ['removeformat undo redo', 'outdent indent', 'forecolor backcolor', 'bold italic underline strikethrough blockquote', 'alignleft aligncenter alignright alignjustify', 'table', 'image', 'link unlink', 'fontselect fontsizeselect', 'styleselect', 'superscript subscript', 'ltr rtl', 'code', 'hklBulletBList hklNumberedList']
    */
    customizeToolbar: {
      type: Array,
      required: false,
      default: () => ([]),
    },
    imgSourceId: {
      type: String,
      default: null,
    },
    contentStyle: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalidInput: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      minimizeToolBar: DEFAULT_TOOLBAR,
    }
  },
  computed: {
    ...mapGetters('language', ['currentLanguage']),
    language () {
      return this.currentLanguage || langs.enUS
    },
    reRenderKey () {
      return `${new Date().getTime()}-${this.language}`
    },
    renderKeyWhenLangChange () {
      return `${new Date().getTime()}-${this.language}`
    },
    localContent: {
      get: function () {
        let ref = _.cloneDeep(this.content)
        ref = ref ? ref.replace(/accordion-content/g, 'accordion--content') : ''
        ref = this.postProcessContent(ref)
        return ref
      },
      set: function (value) {
        let ref = value
        if (ref) {
          // filter the accordion-content, if someone copy the accordion-content style include display: none -- joe 2018.12.14
          ref = ref.replace(/accordion-content/g, 'accordion--content')
          ref = this.postProcessContent(ref)
        }
        this.$emit('syncContent', ref)
      },
    },
    minimizeToolBarArr () {
      return _.flattenDeep(this.minimizeToolBar.map(item => item.split(' ')))
    },
    toolbarOpts () {
      let result = _.cloneDeep(this.minimizeToolBar)
      if (!_.isEmpty(this.customizeToolbar)) {
        result = this.customizeToolbar
      } else if (Array.isArray(this.additionalToolbar)) {
        this.additionalToolbar.forEach((value) => {
          if (!this.minimizeToolBarArr.includes(value)) result.push(value)
        }, [])
      }
      return result.join(' | ')
    },
    pluginsOpts () {
      const pluginsList = [
        'autolink,code,paste',
        'code,paste,colorpicker,fullscreen',
        'link',
        'wordcount',
        'table',
        'image',
        'directionality',
      ]
      return pluginsList
    },
    initConfig () {
      const selfComponent = this
      tinymce.addI18n('zh-CN', zhCN)
      const defaultConfig = {
        convert_urls: false,
        toolbar_sticky: true,
        toolbar_mode: 'sliding',
        autoresize_bottom_margin: 10,
        body_class: 'pmsr-tinymce-body',
        skin: false,
        content_css: false,
        content_style: '.pmsr-tinymce-body {font-family: Verdana;font-size: 14px;line-height: 1.43;letter-spacing: 0;color: #0C3A58;} ul,ol { list-style-position: inside }' + '\n' + this.contentStyle.toString(),
        height: this.height,
        object_resizing: false,
        menubar: false,
        font_formats: [
          'Akzidenz-Grotesk BQ Regular=Akzidenz-Grotesk BQ',
          'Andale Mono=andale mono,times',
          'Arial=arial,helvetica,sans-serif',
          'Arial Black=arial black,avant garde',
          'Book Antiqua=book antiqua,palatino',
          'Comic Sans MS=comic sans ms,sans-serif',
          'Courier New=courier new,courier',
          'Georgia=georgia,palatino',
          'Helvetica=helvetica',
          'Impact=impact,chicago',
          'Symbol=symbol',
          'Tahoma=tahoma,arial,helvetica,sans-serif',
          'Terminal=terminal,monaco',
          'Times New Roman=times new roman,times',
          'Trebuchet MS=trebuchet ms,geneva',
          'Verdana=verdana,geneva',
          'Webdings=webdings',
          'Wingdings=wingdings,zapf dingbats',
          '华文细黑=STXihei',
          '新细明体=PMingLiU',
          '标楷体=DFKai-SB',
        ].join('; '),
        end_container_on_empty_block: true,
        powerpaste_word_import: 'clean',
        code_dialog_height: 450,
        code_dialog_width: 1000,
        advlist_bullet_styles: 'square',
        advlist_number_styles: 'default',
        default_link_target: '_blank',
        link_title: false,
        br_in_pre: false,
        forced_root_block: 'div',
        imagetools_toolbar: 'imageoptions',
        file_picker_types: 'image',
        automatic_uploads: true,
        formats: {
          aligncenter: [
            {
              selector: 'table',
              attributes: { align: 'center' },
            },
            // The below formates is from offical
            // https://github.com/tinymce/tinymce/blob/0d4c349744/modules/tinymce/src/core/main/ts/fmt/DefaultFormats.ts
            {
              selector: 'figure,p,h1,h2,h3,h4,h5,h6,td,th,tr,div,ul,ol,li,pre',
              styles: {
                textAlign: 'center',
              },
              inherit: false,
              preview: 'font-family font-size',
            },
            {
              selector: 'figure.image',
              collapsed: false,
              classes: 'align-center',
              ceFalseOverride: true,
              preview: 'font-family font-size',
            },
            {
              selector: 'img,audio,video',
              collapsed: false,
              styles: {
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
              },
              preview: false,
            },
          ],
        },
        indent_use_margin: true, // Because the outlook of windows ignores the element's padding
        indentation: '40px',
        table_advtab: false,
        table_default_styles: {
          'border-collapse': 'collapse',
          width: '100%',
        },
        file_picker_callback: function (cb, value, meta) {
          let input = document.createElement('input')
          input.setAttribute('type', 'file')
          input.setAttribute('accept', 'image/*')
          // Note: In modern browsers input[type="file"] is functional without
          // even adding it to the DOM, but that might not be the case in some older
          // or quirky browsers like IE, so you might want to add it to the DOM
          // just in case, and visually hide it. And do not forget do remove it
          // once you do not need it anymore
          input.onchange = function () {
            let file = this.files[0]
            let reader = new FileReader()
            reader.onload = function () {
              // Note: Now we need to register the blob in TinyMCEs image blob
              // registry. In the next release this part hopefully won't be
              // necessary, as we are looking to handle it internally.
              let id = 'blobid' + (new Date()).getTime()
              let blobCache = tinymce.activeEditor.editorUpload.blobCache // eslint-disable-line
              let base64 = reader.result.split(',')[1]
              let blobInfo = blobCache.create(id, file, base64, file.name)
              blobCache.add(blobInfo)
              // call the callback and populate the Title field with the file name
              cb(blobInfo.blobUri(), { title: file.name })
            }
            reader.readAsDataURL(file)
          }
          input.click()
        },
        images_upload_handler: async function (blobInfo, success, failure) {
          let formData = new FormData()
          formData.append('file', blobInfo.blob())
          formData.append('srcfilename', blobInfo.name())
          formData.append('src', selfComponent.imgSourceId)
          const response = await uploadImage(formData)
          const resData = _.get(response, 0, null)
          if (!resData) {
            throw new Error(`There is no uploaded file information from the API, ${JSON.stringify(response)}`)
          } else {
            const arr = resData.filename.split('.')
            const suffix = arr[arr.length - 1]
            const imageSrc = `${useBaseUrl()}/static/${resData.checksum}.${suffix}`
            success(imageSrc)
          }
        },
        init_instance_callback: (editor) => {
          editor.on('KeyUp', () => {
            formUtils.setDirty()
          })
        },
        setup (editor) {
          editor.on('ExecCommand', (e) => {
            const refEditor = e.target
            const selectedNode = refEditor.selection.getNode()
            const isJustifyImg = util.isJustifyCmd(e.command) && util.isImgNode(selectedNode)
            if (isJustifyImg) {
              util.justifyImgSelection(editor, e.command, selectedNode)
            }
          })
          editor.on('BeforeExecCommand', (e) => {
            const cmd = e.command.toLowerCase()
            const refEditor = e.target
            const selectedNode = refEditor.selection.getNode()
            const isListOrListItem = util.isListsNode(selectedNode) || util.isListChildNode(selectedNode)
            const isIndentCmd = cmd === 'indent'
            const isOutdentCmd = cmd === 'outdent'
            if (isListOrListItem && isIndentCmd) {
              util.indentListSelection(refEditor)
            } else if (isListOrListItem && isOutdentCmd) {
              util.outdentListSelection(refEditor)
            }
          })
          editor.on('keydown', (e) => {
            if (e.code === 'Backspace') {
              const dom = editor.dom
              const selectionStartElm = editor.selection.getStart()
              if (dom.isEmpty(selectionStartElm) && util.isListChildNode(editor.selection.getNode())) {
                editor.undoManager.transact(() => {
                  dom.remove(editor.selection.getNode())
                  if (dom.isEmpty(editor.selection.getStart()) && util.isListsNode(editor.selection.getNode())) {
                    dom.remove(editor.selection.getStart())
                  }
                  e.preventDefault()
                })
              }
            }
          })
          editor.ui.registry.addToggleButton('hklBulletBList', {
            icon: 'unordered-list',
            active: false,
            tooltip: 'Bullet list',
            onAction: (api) => util.toggleHKLList({
              editor,
              api,
              targetListNodeName: util.UL_ELEMENT_NAME,
            }),
            onSetup: (api) => util.toggleHKLListState(editor, util.UL_ELEMENT_NAME, api),
          })
          editor.ui.registry.addToggleButton('hklNumberedList', {
            icon: 'ordered-list',
            active: false,
            tooltip: 'Numbered list',
            onAction: (api) => util.toggleHKLList({
              editor,
              api,
              targetListNodeName: util.OL_ELEMENT_NAME,
            }),
            onSetup: (api) => util.toggleHKLListState(editor, util.OL_ELEMENT_NAME, api),
          })
        },
      }
      if (this.language === langs.enUS) return defaultConfig
      return Object.assign({
        language: this.language,
      }, defaultConfig)
    },
  },
  methods: {
    postProcessContent (val) {
      return val
    },
  },
}
</script>

<style lang='scss' scoped>
.hkl-invalid-input,
// Support fusion check
.fusion-input-check-is-invalid-input {
  ::v-deep {
    .tox-edit-area__iframe {
      background-color: #f9ecea;
    }
  }
}

</style>
